import { Form, Input } from "antd"
import { AuthProvider } from "components/providers/Forms.provider"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { H2 } from "components/ui/typography/titles/Titles"
import { ValidatePassword } from "components/ui/validatePassword/validatePassword"
import { useValidatePassword } from "hooks/validatePassword.hook"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useResetPasswordConfirm } from "hooks/api/resetPassword/resetPassword.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useNavigate } from "react-router-dom"
import { CustomNavLink } from "components/ui/CustomNavLink/CustomNavLink"
import style from "./NewPassword.module.scss"

const { title, navLink } = style

export const NewPassword = () => {
	const [passwordChanged, setPasswordChanged] = useState(false)
	const navigate = useNavigate()

	const [submittable, setSubmittable] = useState(false)
	const { formatError } = useApiErrorFormatter()

	const { mutate: resetPasswordConfirmMutation } = useResetPasswordConfirm()

	const [searchParams] = useSearchParams()
	const uid = searchParams.get("uid")
	const token = searchParams.get("token")

	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	const validatedPassword = useValidatePassword(values)

	// Check if the password and reNewPassword fields are valid
	// and if they match, then set the submittable state
	useEffect(() => {
		const allKeysAreValid = Object.values(validatedPassword).every((val) => val)
		const passwordMatch = values?.password === values?.reNewPassword

		setSubmittable(passwordMatch && allKeysAreValid)
	}, [validatedPassword, values])

	const onSubmitHandle = () => {
		resetPasswordConfirmMutation(
			{ uid, token, password: values?.password, reNewPassword: values?.reNewPassword },
			{
				onSuccess: () => {
					setPasswordChanged(true)
				},
				onError: (e) => {
					formatError(e)
				},
			},
		)
	}

	return (
		<>
			{passwordChanged ? (
				<>
					<H2 className={title}>Password has been successfully changed!</H2>
					<ButtonPrimary onClick={() => navigate("/")}>Back to log in</ButtonPrimary>
				</>
			) : (
				<>
					<H2 className={title}>Enter a new password</H2>
					<AuthProvider>
						<Form form={form} name="formPassword" size="large">
							<Form.Item name="password" rules={[{ required: true }]}>
								<Input.Password placeholder="Password" />
							</Form.Item>
							<Form.Item
								name="reNewPassword"
								rules={[
									{
										required: true,
										message: "Please confirm your password",
									},
									({ getFieldValue }) => ({
										validator(_, value) {
											if (!value || getFieldValue("password") === value) {
												return Promise.resolve()
											}
											return Promise.reject(
												new Error("Passwords do not match. Please enter the same password in both fields"),
											)
										},
									}),
								]}
							>
								<Input.Password placeholder="Confirm Password" />
							</Form.Item>
							<ValidatePassword validate={validatedPassword} />
							<ButtonPrimary onClick={onSubmitHandle} submittable={submittable}>
								Continue
							</ButtonPrimary>
						</Form>
					</AuthProvider>
					<CustomNavLink className={navLink} to="/">
						Wait, I remember my password
					</CustomNavLink>
				</>
			)}
		</>
	)
}
